import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const CARD_ANIM = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const intendedPlan = searchParams.get("plan"); // "pro" | "pm" | null
  const intendedInterval = searchParams.get("interval") || "monthly"; // monthly | 6mo | 12mo
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      const pendingInvite = sessionStorage.getItem("hg_pending_invite");
      if (pendingInvite) {
        navigate(`/accept-invite?token=${pendingInvite}`);
        return;
      }
      // Preserve a plan+interval selection made before signing in and continue
      // straight to Stripe checkout for that subscription.
      if (intendedPlan === "pro" || intendedPlan === "pm") {
        try {
          const res = await api.post("/checkout/session", {
            plan_id: intendedPlan,
            billing_interval: intendedInterval,
            origin_url: window.location.origin,
          });
          if (res.data?.url) {
            trackEvent("checkout_started", "/login");
            window.location.href = res.data.url;
            return;
          }
        } catch (checkoutErr) {
          toast.error(getErrorMessage(checkoutErr, "Signed in, but checkout couldn't start. You can upgrade from Pricing."));
          navigate("/app/pricing");
          return;
        }
      }
      const dest = location.state?.from || "/app/dashboard";
      navigate(dest);
    } catch (err) {
      toast.error(getErrorMessage(err, "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <Logo dark />
        <div>
          <h2 className="font-display text-3xl font-semibold leading-tight">Smarter booking risk checks, without the blacklist.</h2>
          <p className="mt-4 max-w-md text-primary-foreground/70">Sign in to assess bookings, review your history and protect your property — the compliant way.</p>
        </div>
        <p className="text-xs text-primary-foreground/50">HostGuard provides booking risk guidance only. It does not maintain a guest blacklist.</p>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <motion.div {...CARD_ANIM} className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h1 className="font-display text-2xl font-semibold">Log in to HostGuard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back. Enter your details to continue.</p>

          {(intendedPlan === "pro" || intendedPlan === "pm") && (
            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-sm text-foreground" data-testid="login-plan-notice">
              Sign in to continue to secure checkout for <strong>{intendedPlan === "pro" ? "Pro Host" : "Property Manager"}</strong>.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account? <Link to={`/signup${location.search}`} className="font-medium text-primary hover:underline" data-testid="login-to-signup-link">Sign up free</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
